/**
 * The inspection tools, and why they are behind one switch.
 *
 * A wireframe, a grid, a bounding box and a section plane are the four things
 * somebody uses when they have stopped *looking* at a model and started
 * *checking* one. That is a different task from the one the viewer's main
 * controls serve, and putting these beside "Modele sığdır" would hand every
 * customer four controls that only mean something to a person who already
 * knows what a manifold edge is.
 *
 * So they live together, behind a disclosure, and the viewer opens without
 * them. The brief's own words: the main interface must not turn into a
 * technical research panel.
 *
 * **None of them changes the model.** Every one is a property of the frame
 * being drawn - the clipping plane discards fragments, it does not cut
 * geometry, and an export taken while a section is open contains the whole
 * surface. That is enforced by where the state lives: here, in the viewer,
 * with no path to the artifact.
 *
 * Persisted per browser rather than per account, for the same reason
 * `viewerPreferences` gives: whether somebody wants a wireframe is a property
 * of what they are doing at that screen, and storing it on a user row would
 * make a phone inherit a decision made at a workstation.
 */

/** Which way a section plane faces. Model space, not screen space. */
export const CLIP_AXES = ["x", "y", "z"] as const;
export type ClipAxis = (typeof CLIP_AXES)[number];

export interface InspectionState {
  /** Draw the triangle edges over the surface. */
  wireframe: boolean;
  /** A ground plane under the model, for orientation and for scale. */
  grid: boolean;
  /** The three coloured model-space axes at the model's own origin. */
  axes: boolean;
  /** The model's bounding box, as twelve lines. */
  boundingBox: boolean;
  /** Whether a section plane is cutting the view. */
  clipping: boolean;
  clipAxis: ClipAxis;
  /**
   * Where the plane sits, 0 to 1 across the model's extent on that axis.
   * A fraction rather than a coordinate so the control means the same thing
   * on a model in metres and one in a solve's arbitrary units.
   */
  clipPosition: number;
  /** Which side of the plane is hidden. */
  clipFlipped: boolean;
}

export const DEFAULT_INSPECTION: InspectionState = {
  wireframe: false,
  grid: false,
  axes: false,
  boundingBox: false,
  clipping: false,
  clipAxis: "x",
  clipPosition: 0.5,
  clipFlipped: false,
};

const STORAGE_KEY = "sentycap.viewer.inspection";

/**
 * Read the remembered tools.
 *
 * Every field is checked individually rather than the object being trusted.
 * The stored value outlives this build: a release that adds a tool must read
 * last week's object without it, and one that removes a tool must not be
 * handed a value it no longer understands.
 */
export function readInspection(): InspectionState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_INSPECTION;
    const stored = JSON.parse(raw) as Partial<InspectionState>;
    return {
      wireframe: stored.wireframe === true,
      grid: stored.grid === true,
      axes: stored.axes === true,
      boundingBox: stored.boundingBox === true,
      clipping: stored.clipping === true,
      clipAxis: (CLIP_AXES as readonly string[]).includes(stored.clipAxis ?? "")
        ? (stored.clipAxis as ClipAxis)
        : DEFAULT_INSPECTION.clipAxis,
      clipPosition:
        typeof stored.clipPosition === "number" &&
        Number.isFinite(stored.clipPosition) &&
        stored.clipPosition >= 0 &&
        stored.clipPosition <= 1
          ? stored.clipPosition
          : DEFAULT_INSPECTION.clipPosition,
      clipFlipped: stored.clipFlipped === true,
    };
  } catch {
    return DEFAULT_INSPECTION;
  }
}

export function writeInspection(state: InspectionState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Applied for this session already. A browser refusing storage is not
    // worth interrupting somebody who is inspecting a model.
  }
}
